import { IconName } from "@inflow/icons"
import { createLink, LinkComponent } from "@tanstack/react-router"
import { PropsWithChildren, useRef } from "react"
import { forwardRef } from "react"
import {
  mergeProps,
  useFocusRing,
  useHover,
  useLink,
  useObjectRef,
} from "react-aria"
import type { AriaLinkOptions } from "react-aria"
import {
  GridListItem,
  GridListItemProps,
  Link,
  ProgressBar,
  Button as RACButton,
  ButtonProps as RACButtonProps,
} from "react-aria-components"
import { useTranslation } from "react-i18next"

import { cn } from "@/utilities/etc"
import { useTheme } from "@/utilities/hooks/useTheme"

import { buttonClasses, ButtonStylingProps } from "./Button.helpers"
import styles from "./Button.module.css"
import Icon from "./Icon"

export type ButtonProps = Omit<RACButtonProps, "children"> & {
  className?: string
} & ButtonStylingProps

/**
 * Button with semantic theming support
 *
 * @example
 * // Basic usage (recommended)
 * <Button>Submit</Button>                    // Uses theme's default config
 * <Button buttonType="primary">Primary</Button>  // Primary button type
 *
 * // Button types (maps through theme tokens)
 * <Button buttonType="primary">Primary</Button>     // theme.button.primary
 * <Button buttonType="success">Success</Button>     // theme.button.success
 * <Button buttonType="danger">Delete</Button>      // theme.button.danger
 * <Button buttonType="caution">Warning</Button>     // theme.button.caution
 *
 * // Presentation styles
 * <Button presentation="ghost">Ghost</Button>
 * <Button presentation="link">Link</Button>
 *
 * // Combinations
 * <Button buttonType="primary" presentation="ghost">Combo</Button>
 *
 * @param buttonType - Semantic button type (primary, default, success, danger, caution). Optional - defaults to "default"
 * @param presentation - Style (filled, ghost, link). Optional - defaults to "filled"
 */
export const Button: React.FC<PropsWithChildren<ButtonProps>> = (props) => {
  const { theme, documentThemeMode } = useTheme()
  const buttonTheme = documentThemeMode === "root" ? undefined : theme

  const ref = useRef<HTMLButtonElement | null>(null)
  const classes = cn(
    styles.button,
    ...buttonClasses({
      styling: props,
      theme: buttonTheme,
    }),
    props.className
  )

  return (
    <RACButton
      {...props}
      className={classes}
      ref={ref}
      data-disabled={props.isDisabled || props.isPending || undefined}
      data-pending={props.isPending}
    >
      {props.isPending && <ButtonLoader />}
      {props.children}
    </RACButton>
  )
}

export type RACLinkProps = Omit<AriaLinkOptions, "href"> & {
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
} & ButtonStylingProps &
  (
    | {
        iconName: IconName
        "aria-label": string
      }
    | {
        iconName?: never
      }
  )

export const RACLink = forwardRef<HTMLAnchorElement, RACLinkProps>(
  (props, forwardedRef) => {
    const ref = useObjectRef(forwardedRef)

    const { isPressed, linkProps } = useLink(props, ref)
    const { isHovered, hoverProps } = useHover(props)
    const { isFocusVisible, isFocused, focusProps } = useFocusRing(props)

    const {
      isDisabled: _isDisabled,
      iconName: _iconName,
      buttonType: _buttonType,
      forced: _forced,
      ...rest
    } = props
    const { theme, documentThemeMode } = useTheme()
    const buttonTheme = documentThemeMode === "root" ? undefined : theme

    const classes = cn(
      props.className,
      styles.linkButton,
      ...buttonClasses({
        styling: props,
        theme: buttonTheme,
      })
    )

    return (
      <Link
        {...mergeProps(linkProps, hoverProps, focusProps, rest)}
        className={classes}
        ref={ref}
        data-hovered={isHovered || undefined}
        data-pressed={isPressed || undefined}
        data-focus-visible={isFocusVisible || undefined}
        data-focused={isFocused || undefined}
      />
    )
  }
)

export const RACGridListItem = forwardRef<HTMLDivElement, GridListItemProps>(
  (props, forwardedRef) => {
    const ref = useObjectRef(forwardedRef)

    return <GridListItem {...props} ref={ref} />
  }
)

const CreatedRACLink = createLink(RACLink)
export const CreatedRACGridListItem = createLink(RACGridListItem)

export const LinkButton: LinkComponent<typeof RACLink> = (props) => {
  return <CreatedRACLink {...props} />
}

export const LinkGridListItem: LinkComponent<typeof RACGridListItem> = (
  props
) => {
  return <CreatedRACGridListItem {...props} />
}

export type IconButtonProps = ButtonProps & {
  iconName: IconName
  "aria-label": string
}

export const IconButton: React.FC<IconButtonProps> = (props) => {
  const { theme, documentThemeMode } = useTheme()
  const buttonTheme = documentThemeMode === "root" ? undefined : theme

  const ref = useRef<HTMLButtonElement | null>(null)
  const classes = cn(
    props.className,
    styles.iconButton,
    ...buttonClasses({
      styling: props,
      theme: buttonTheme,
    })
  )

  return (
    <RACButton
      {...props}
      className={classes}
      ref={ref}
      data-disabled={props.isDisabled || props.isPending || undefined}
    >
      {props.isPending ? <ButtonLoader /> : <Icon name={props.iconName} />}
    </RACButton>
  )
}

export const LinkIconButton: LinkComponent<typeof RACLink> = (props) => {
  if (!props.iconName) {
    console.error("No iconName provided")
    return <></>
  }
  return (
    <LinkButton {...props}>
      <Icon name={props.iconName} />
    </LinkButton>
  )
}

export const ButtonLoader = (props: {
  style?: React.CSSProperties
  size?: number
}) => {
  const size = props.size || 24
  const center = size / 2
  const strokeWidth = 2
  const r = center - strokeWidth
  const c = 2 * r * Math.PI
  const { t } = useTranslation()

  return (
    <ProgressBar
      {...props}
      aria-label={t("common.loading")}
      isIndeterminate
      className={styles.loader}
    >
      <svg
        width={"1.25rem"}
        height={"1.25rem"}
        viewBox={`0 0 ${size} ${size}`}
        fill="none"
        strokeWidth={strokeWidth}
      >
        <circle
          cx={center}
          cy={center}
          r={r - (strokeWidth / 2 - 0.75)}
          opacity={0.5}
        />
        <circle
          cx={center}
          cy={center}
          r={r}
          strokeDasharray={`${c} ${c}`}
          strokeDashoffset={c - 0.5 * c}
          strokeLinecap="round"
        />
      </svg>
    </ProgressBar>
  )
}
